import React from 'react';
import { Component } from 'react';
import { ImageBackground, SafeAreaView, StyleSheet, View, Text, TouchableWithoutFeedback, ScrollView, ActivityIndicator } from 'react-native';
import Theme from '../../theme';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { defaultScale, scaleText } from '../../utils/scaling';
import progressActor from '../../assets/images/progress-actor-banner.png';
import FitillionIcon from '../../assets/icons/fitillion.svg';
import { LinearGradient } from '../../ui/components/LinearGradient';
import { loadSelectMemberScreen, selectMember } from '../../FamilyMembers/actions';
import { UserServiceClient } from 'fitbeat.api-clients';
import auth from '@react-native-firebase/auth';
import { IAppStore } from '../../HomeWorkouts';
import { familyMembersSelector, isSwitchingToUserSelector, ISwitchMemberUserProfile, switchMemberScreenLoadingSelector } from '../../FamilyMembers';
import { profileColors } from 'fitbeat.models';
import { InitialsCircle } from '../../ui/components/InitialsCircle';
import SwitchIcon from '../../assets/icons/switch.svg';
import { getApiConfig } from '../../config';
import { AuthTokenBuilder } from '../../utils/authTokenBuilder';
import { Logger, LogoutHelper } from '../../utils';

interface IProps {
  t: any;
  familyMembers: ISwitchMemberUserProfile[];
  isSwitchingToUser: boolean;
  navigation: any;
  isLoading: boolean;
  loadScreen: () => void;
  selectMember: (swoppingToUser: ISwitchMemberUserProfile) => void;
}

class SelectMember extends Component<IProps> {
  constructor(props) {
    super(props);
    Logger.leaveBreadcrumb('Elite:SelectMember');
  }

  public componentDidMount = async () => {
    this.props.loadScreen();
  }

  public logout = async () => {
    await LogoutHelper();
    this.props.navigation.navigate('Welcome');
  }

  public render() {
    const { t } = this.props;
    return (
      <SafeAreaView style={styles.SafeAreaViewContainer}>
        {this.props.isLoading && (
          <ActivityIndicator
            size='small'
            color={Theme.PRIMARY}
            animating={true}
            style={styles.loader}
          />
        )}

        {!this.props.isLoading && (
        <View style={styles.container}>
          <View style={styles.banner}>
            <ImageBackground
              source={progressActor}
              style={styles.image}
              resizeMode='cover'
            >
              <LinearGradient
                colors={['rgba(0, 0, 0, 0)', 'rgba(0, 0, 0, 100)']}
                style={styles.gradient}
                start={{ x: 0, y: 0 }}
                end={{ x: 0, y: 1 }}
              />
              <View style={styles.icon}>
                <FitillionIcon
                  width={230}
                  height={230}
                  color={Theme.WHITE}
                  fill={Theme.WHITE}
                />
              </View>
              <Text style={styles.mainHeader}>{t('selectMember')}</Text>
            </ImageBackground>
          </View>
        <ScrollView style={{marginTop: 19}}>
          <>
            {this.props.familyMembers.map((member) => {
              const profileColor = member.familyPlanSettings
                ? member.familyPlanSettings.profileColor
                : profileColors[0];
              return (
                <TouchableWithoutFeedback
                  disabled={this.props.isSwitchingToUser}
                  onPress={() => this.props.selectMember(member)}
                >
                  <View
                    style={
                      this.props.isSwitchingToUser
                        ? member.isCurrentLoggedInUser
                          ? {
                              ...styles.memberContainer,
                              backgroundColor: Theme.DARK_CHARCOAL,
                            }
                          : { ...styles.memberContainer, opacity: 0.3 }
                        : styles.memberContainer
                    }
                  >
                    <View style={styles.userDetailContainer}>
                      <View style={styles.userIconContainer}>
                        <InitialsCircle
                          initials={member.initials}
                          backgroundColor={profileColor ?? profileColors[0]}
                          color={Theme.WHITE}
                          size={80}
                          containerStyle={styles.userIconContainer}
                          textStyle={styles.userIconText}
                        />
                        <View style={styles.userDetail}>
                          <View style={{ flexDirection: 'row' }}>
                            <Text
                              style={
                                this.props.isSwitchingToUser &&
                                member.isCurrentLoggedInUser
                                  ? {
                                      ...styles.userDetailText,
                                      color: Theme.WHITE,
                                    }
                                  : styles.userDetailText
                              }
                            >
                              {member.firstname}
                            </Text>
                          </View>
                        </View>
                      </View>
                    </View>
                    <View style={styles.settingsIcon}>
                      {this.props.isSwitchingToUser &&
                      member.isCurrentLoggedInUser ? (
                        <Text style={styles.switching}>
                          {t('switching')}...
                        </Text>
                      ) : (
                        <>
                          <SwitchIcon
                            color={Theme.BLACK}
                            width={50}
                            height={50}
                          />
                        </>
                      )}
                    </View>
                  </View>
                </TouchableWithoutFeedback>
              );
            })}
          </>
        </ScrollView>
        <View>
          <Text
            onPress={this.props.isSwitchingToUser ? undefined : () => this.logout()}
            style={this.props.isSwitchingToUser ? {...styles.signOut, opacity: 0.3} : styles.signOut}
          >
            {t('signOutOfAllAccounts')}
          </Text>
        </View>
        </View>)}
      </SafeAreaView>
    );
  }
}

const styles = StyleSheet.create({
  SafeAreaViewContainer: {
    flex: 1,
    backgroundColor: Theme.WHITE,
  },
  container: {
    flex: 1,
  },
  loader: {
    flex: 1,
    paddingVertical: defaultScale(20),
    alignItems: 'center',
  },
  banner: {
    height: 330,
  },
  image: {
    aspectRatio: 2.4,
    width: '100%',
  },
  icon: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 50,
  },
  mainHeader: {
    color: Theme.WHITE,
    lineHeight: 67,
    fontSize: 44,
    fontFamily: Theme.Custom700,
    position: 'absolute',
    bottom: defaultScale(15),
    left: 37,
  },
  gradient: {
    ...StyleSheet.absoluteFill,
  },
  memberContainer: {
    marginLeft: 37,
    marginRight: 37,
    borderWidth: 0.3,
    borderColor: Theme.GREY_DARK,
    marginTop: 26,
    flexDirection: 'row',
    borderRadius: 5,
    height: 150,
  },
  userDetailContainer: {
    flex: 1,
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  userIconContainer: {
    flexDirection: 'row',
    marginLeft: defaultScale(10),
    alignItems: 'center',
  },
  userIconText: {
    color: Theme.WHITE,
    fontFamily: Theme.Weight700,
    fontSize: 30,
    lineHeight: 27,
  },
  userDetail: {
    justifyContent: 'center',
  },
  userDetailText: {
    ...Theme.textStyle.bodyCopyBold,
    fontSize: 30,
    lineHeight: 28,
    marginLeft: 16,
  },
  settingsIcon: {
    alignSelf: 'center',
    marginVertical: defaultScale(3),
    marginRight: defaultScale(20),
  },
  switching: {
    fontFamily: Theme.Weight100Italic,
    fontSize: 30,
    color: Theme.WHITE,
  },
  signOut: {
    textAlign: 'center',
    color: Theme.PRIMARY,
    marginTop: defaultScale(15),
    marginBottom: 51.5,
    fontFamily: Theme.Weight700,
    fontSize: 34,
    lineHeight: 40.5,
  },
});

const mapDispatchToProps = (dispatch: any, ownProps: any) => {
  const userServiceClient = new UserServiceClient(getApiConfig(), fetch, Logger.debug, new AuthTokenBuilder());
  const currentLoggedInUserId = auth().currentUser.uid;
  return {
    loadScreen: () => dispatch(loadSelectMemberScreen(currentLoggedInUserId, userServiceClient)),
    selectMember: (swoppingToUser: ISwitchMemberUserProfile) => dispatch(selectMember(swoppingToUser, currentLoggedInUserId, userServiceClient, ownProps.navigation)),
  };
};

const mapStateToProps = (state: IAppStore) => {
  return {
    familyMembers: familyMembersSelector(state),
    isSwitchingToUser: isSwitchingToUserSelector(state),
    isLoading: switchMemberScreenLoadingSelector(state),
  };
};

export default translate(['familyPlan'])(
  connect(mapStateToProps, mapDispatchToProps)(SelectMember),
);
